<template>

<div class="progress-row" :style="{ width: progressRowWidth }" v-tooltip="tooltipContent">
  
  <div v-if="showStages" class="stage-label">{{ stageLabel }}</div>

  <div class="circular-progress-container">
    <!-- Error/Terminated cross icon -->
    <div v-if="isError || isTerminated" class="error-cross-icon">
      <svg width="55" height="55" viewBox="0 0 55 55">
        <line x1="15" y1="15" x2="40" y2="40" stroke="#d32f2f" stroke-width="3" stroke-linecap="butt"/>
        <line x1="40" y1="15" x2="15" y2="40" stroke="#d32f2f" stroke-width="3" stroke-linecap="butt"/>
      </svg>
    </div>
    <div v-else-if="isDeleted" class="deleted-line-icon">
      <svg width="55" height="55" viewBox="0 0 55 55">
        <line x1="15" y1="27.5" x2="40" y2="27.5" stroke="#d32f2f" stroke-width="4" stroke-linecap="butt"/>
      </svg>
    </div>
    <!-- Complete checkmark icon -->
    <div v-else-if="isComplete" class="complete-checkmark-icon">
      <svg width="55" height="55" viewBox="0 0 55 55">
        <polyline points="15,28 24,37 40,18" stroke="#2e7d32" stroke-width="3" stroke-linecap="butt" stroke-linejoin="miter" fill="none"/>
      </svg>
    </div>
    <!-- Circular Progress with stage label -->   
      <div v-else  :class="['circular-progress', { 'rotating': isWaiting }]">
        <svg width="55" height="55" viewBox="0 0 55 55">
          <circle
            cx="27.5"
            cy="27.5"
            r="22"
            fill="none"
            stroke="#eeeeee"
            stroke-width="5"
          />
          <circle
            cx="27.5"
            cy="27.5"
            r="22"
            fill="none"
            :stroke="isWaiting ? props.waitingStrokeColor : props.strokeColor"
            stroke-width="5"
            :stroke-dasharray="isWaiting ? circumference * 0.25 + ' ' + circumference * 0.75 : circumference"
            :stroke-dashoffset="isWaiting ? 0 : progressOffset"
            stroke-linecap="butt"
            transform="rotate(-90 27.5 27.5)"
          />
        </svg>
        <div class="percentage-text">{{ isWaiting ? '...' : Math.round(percent || 0) + '%' }}</div>
       
        <div 
          v-if="!isWaiting && stageNumber && totalStages" 
          class="stage-counter-text"
        >
          {{ stageNumber }} / {{ totalStages }}
        </div>
      </div>
    </div>
  </div>

</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  percent: {
    type: Number,
    default: 0,
  },
  stageLabel: {
    type: String,
    default: null,
  },
  stageNumber: {
    type: Number,
    default: null,
  },
  totalStages: {
    type: Number,
    default: null,
  },
  strokeColor: {
    type: String,
    default: '#2e7d32',
  },
  waitingStrokeColor: {
    type: String,
    default: '#1565C0',
  },
  status: {
    type: String,
    default: null,
  },
  showStages: {
    type: Boolean,
    default: true,
  },
  taskUID: {
    type: String,
    default: null,
  },
  userName: {
    type: String,
    default: null,
  },
  parentUID: {
    type: String,
    default: null,
  },
});
const circumference = computed(() => 2 * Math.PI * 22); // radius is 22

const tooltipContent = computed(() => {
  const parts = [];
  if (props.taskUID) parts.push(`Task: ${props.taskUID}`);
  if (props.userName) parts.push(`User: ${props.userName}`);
  if (props.parentUID) parts.push(`Parent: ${props.parentUID}`);
  return parts.length > 0 ? parts.join(' | ') : null;
});

const progressRowWidth = computed(() => {
  return props.showStages ? '150px' : 'fit-content';
});

const progressOffset = computed(() => {
  const percent = props.percent || 0;
  return circumference.value - (percent / 100) * circumference.value;
});

const isError = computed(() => {
  return props.status?.toLowerCase().includes('error') || false;
});

const isTerminated = computed(() => {
  return props.status?.toLowerCase().includes('terminate') || false;
});

const isDeleted = computed(() => {
  return props.status?.toLowerCase().includes('deleted') || false;
});

const isComplete = computed(() => {
  return props.status?.toLowerCase().includes('complete') || false;
});

const isWaiting = computed(() => {
  const status = props.status?.toLowerCase() || '';
  return status === 'init' || status === 'queued' || status === 'pending';
});

</script>

<style lang="scss" scoped>
.circular-progress-container {

  align-items: center;
  gap: 0;
  margin-left: auto;
  min-height: 55px;
}

.stage-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #000;
  text-align: right;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  line-height: 1.2;
  margin-bottom: 4px;
  min-height: 14px;
}

.progress-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-direction: row;
  cursor: help;
}

.stage-label-left {
  font-size: 0.7rem;
  font-weight: 600;
  color: #000;
  width: 100%;
  text-align: right;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  line-height: 1.2;
}

.circular-progress {
  position: relative;
  width: 55px;
  height: 55px;
  
  svg {
    transform: rotate(0deg);
    
    circle {
      transition: stroke-dashoffset 0.3s ease;
    }
  }
  
  &.rotating svg {
    animation: rotate-progress 2s linear infinite;
  }
}

@keyframes rotate-progress {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.percentage-text {
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.7rem;
  font-weight: 500;
  color: #000000;
}

.progress-divider {
  position: absolute;
  top: 52%;
  left: 50%;
  transform: translateX(-50%);
  width: 60%;
  height: 1px;
  background-color: #d0d0d0;
}

.stage-counter-text {
  position: absolute;
  top: 65%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.55rem;
  font-weight: 600;
  color: #000000;
  white-space: nowrap;
}

.error-cross-icon {
  width: 55px;
  height: 55px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.complete-checkmark-icon {
  width: 55px;
  height: 55px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.deleted-line-icon {
  width: 55px;
  height: 55px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
